package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ClassDecl
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.TypeAst
import top.wcpe.allincore.dsl.compiler.ast.dataClassDecls
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * M6-b：容器形参 + source 属性的语义校验。
 *
 * 入口前提（已在 [top.wcpe.allincore.dsl.compiler.parse.ElementBlockParser] 阶段完成）：
 *  - E700 / E701（形参与 source 同时存在性）已在 parse 期拦下
 *  - E703 形参数 >=3 也在 parse 期拦下
 *
 * 本 checker 集中负责：
 *  - E702：source 表达式静态类型不是 List 或 Map
 *  - E703：List → 形参数必须 1；Map → 形参数必须 2
 *  - E704：形参类型与 source 元素类型不匹配（仅在用户显式标注形参类型时）
 *  - E705：source 元素是 data 但缺 id 字段
 *  - E706：source 是 List<Map> / List<Any> / 未知元素类型，且没有显式 key
 *  - W707：嵌套形参遮蔽外层
 *  - E708（M6-c Stage 4）：容器自身属性表达式不得引用本容器 source 形参
 *
 * 同时在 Stage 4 引入"形参类型反向推断"：当形参未显式标注时，按 source 元素
 * 类型回填 [ElementAst.SourceParam.inferredType]，供后续阶段做更精确的类型检查。
 */
class SourceContainerChecker(private val diag: DiagnosticCollector) {

    private val dataDecls: MutableMap<String, ClassDecl> = linkedMapOf()
    /** state 字段名 → 类型标注（仅当 state defaults 显式标注类型时存在）。 */
    private val stateTypes: MutableMap<String, TypeAst> = linkedMapOf()

    fun check(screen: ScreenAst) {
        // putIfAbsent 为 JVM 扩展；common 等价写法（返回值本就未使用，FR-UI-061 中立化）
        for (d in screen.dataClassDecls()) if (d.name !in dataDecls) dataDecls[d.name] = d
        screen.state?.entries?.forEach { e ->
            e.type?.let { stateTypes[e.name] = it }
        }
        walk(screen.root, outerParams = emptySet())
    }

    private fun walk(element: ElementAst, outerParams: Set<String>) {
        val params = element.sourceParams
        val srcExpr = element.sourceExpr

        // W707：嵌套同名形参检测
        for (p in params) {
            if (p.name != "_" && p.name in outerParams) {
                diag.warning(
                    DslError.W707_CONTAINER_PARAM_SHADOWING,
                    p.position,
                    element.kind, p.name,
                )
            }
        }

        // source 类型推断 + 各错误码联动校验
        if (srcExpr != null) {
            checkSource(element, srcExpr, params)
            // E708：容器自身属性不得引用本容器 source 形参
            checkContainerSelfPropsDoNotReferenceSourceParams(element, params)
        }

        // 累积形参名进入子树
        val merged = if (params.isEmpty()) outerParams else outerParams + params.mapNotNull {
            if (it.name == "_") null else it.name
        }
        // RFC-RECOMPOSITION：conditional 包装节点 children 空，但分支体应继承外层 source 形参作用域
        top.wcpe.allincore.dsl.compiler.ast.ElementWalk.directDescendants(element).forEach { walk(it, merged) }
    }

    /** 校验单个容器的 source 表达式与形参约束。 */
    private fun checkSource(
        element: ElementAst,
        srcExpr: ExprAst,
        params: List<ElementAst.SourceParam>,
    ) {
        val srcType = inferSourceType(srcExpr) ?: return // 无法静态判定 → 跳过

        when (srcType) {
            is SourceType.ListT -> {
                if (params.size != 1) {
                    diag.error(
                        DslError.E703_CONTAINER_PARAM_COUNT_MISMATCH,
                        element.position,
                        element.kind, params.size, "source 是 List，要求 1 个形参",
                    )
                    return
                }
                inferSourceParamType(params[0], srcType.elem)
                checkListElement(element, params[0], srcType.elem)
            }
            is SourceType.MapT -> {
                if (params.size != 2) {
                    diag.error(
                        DslError.E703_CONTAINER_PARAM_COUNT_MISMATCH,
                        element.position,
                        element.kind, params.size, "source 是 Map，要求 2 个形参",
                    )
                    return
                }
                inferSourceParamType(params[0], srcType.key)
                inferSourceParamType(params[1], srcType.value)
            }
            is SourceType.Other -> {
                diag.error(
                    DslError.E702_CONTAINER_SOURCE_NOT_ITERABLE,
                    srcExpr.position,
                    element.kind, srcType.display,
                )
            }
        }
    }

    /**
     * M6-c Stage 4：未显式标注的形参，按 source 元素类型回填 [ElementAst.SourceParam.inferredType]。
     *
     * - 已显式标注（[ElementAst.SourceParam.type] != null）：保持 null，显式优先
     * - source 元素类型为 null（如 ListLiteralExpr / 静态无法判定）：填入 `Any` 兜底
     * - 否则填入元素类型本身
     */
    private fun inferSourceParamType(param: ElementAst.SourceParam, elemType: TypeAst?) {
        if (param.type != null) return
        param.inferredType = elemType ?: TypeAst.Named("Any", nullable = false, param.position)
    }

    /**
     * M6-c Stage 4 / E708：容器自身属性表达式不得引用本容器 source 形参。
     *
     * - 范围：当前 element.props 中除 `source` 之外的所有 PropAssign 的值表达式
     * - 触发：表达式（递归）中出现引用本容器形参名的 IdentRef / MemberAccess 等
     * - 子节点引用形参为模板合法用法，本检查仅扫描容器自身 props（不下钻 children）
     */
    private fun checkContainerSelfPropsDoNotReferenceSourceParams(
        element: ElementAst,
        params: List<ElementAst.SourceParam>,
    ) {
        if (params.isEmpty()) return
        val names = params.mapNotNull { if (it.name == "_") null else it.name }.toSet()
        if (names.isEmpty()) return
        for (prop in element.props) {
            // source 自身不属于"容器自身属性"集合：source = item.x 之类的循环依赖
            // 不在本错误码范围内（其它规则负责）。
            // key 是数据驱动容器的"每项标识"prop，按设计必然引用 source 形参，亦排除。
            if (prop.name == "source" || prop.name == "key") continue
            val hit = findSourceParamRef(prop.value, names) ?: continue
            diag.error(
                DslError.E708_CONTAINER_SELF_PROP_REFS_SOURCE_PARAM,
                prop.position,
                element.kind, prop.name, hit,
            )
        }
        for (modifier in element.modifiers) {
            for (field in modifier.fields) {
                val hit = findSourceParamRef(field.value, names) ?: continue
                diag.error(
                    DslError.E708_CONTAINER_SELF_PROP_REFS_SOURCE_PARAM,
                    field.position,
                    element.kind, modifierFieldName(modifier.kind, field.name), hit,
                )
            }
        }
    }

    private fun modifierFieldName(kind: String, field: String): String =
        if (kind == "style") field else "$kind.$field"

    /** 递归扫描表达式 [expr]，命中 [names] 中任意形参名则返回该名字；否则 null。 */
    private fun findSourceParamRef(expr: ExprAst, names: Set<String>): String? {
        when (expr) {
            is ExprAst.IdentRef -> if (expr.name in names) return expr.name
            is ExprAst.LocalRef -> if (expr.name in names) return expr.name
            is ExprAst.MemberAccess -> {
                findSourceParamRef(expr.target, names)?.let { return it }
            }
            is ExprAst.SafeMemberAccess -> {
                findSourceParamRef(expr.target, names)?.let { return it }
            }
            is ExprAst.MemberCall -> {
                findSourceParamRef(expr.target, names)?.let { return it }
                expr.args.forEach { a -> findSourceParamRef(a, names)?.let { return it } }
                expr.namedArgs.forEach { na -> findSourceParamRef(na.value, names)?.let { return it } }
            }
            is ExprAst.SafeMemberCall -> {
                findSourceParamRef(expr.target, names)?.let { return it }
                expr.args.forEach { a -> findSourceParamRef(a, names)?.let { return it } }
            }
            is ExprAst.BinaryOp -> {
                findSourceParamRef(expr.left, names)?.let { return it }
                findSourceParamRef(expr.right, names)?.let { return it }
            }
            is ExprAst.UnaryOp -> findSourceParamRef(expr.operand, names)?.let { return it }
            is ExprAst.Elvis -> {
                findSourceParamRef(expr.left, names)?.let { return it }
                findSourceParamRef(expr.right, names)?.let { return it }
            }
            is ExprAst.Ternary -> {
                findSourceParamRef(expr.cond, names)?.let { return it }
                findSourceParamRef(expr.thenExpr, names)?.let { return it }
                findSourceParamRef(expr.elseExpr, names)?.let { return it }
            }
            is ExprAst.Interp -> for (part in expr.parts) {
                if (part is ExprAst.Interp.InterpPart.ExprPart) {
                    findSourceParamRef(part.expr, names)?.let { return it }
                }
            }
            is ExprAst.ListLiteralExpr -> expr.elements.forEach {
                findSourceParamRef(it, names)?.let { return it }
            }
            is ExprAst.MapLiteralExpr -> expr.entries.forEach {
                findSourceParamRef(it.value, names)?.let { return it }
            }
            is ExprAst.CallExpr -> {
                findSourceParamRef(expr.callee, names)?.let { return it }
                expr.args.forEach { a -> findSourceParamRef(a, names)?.let { return it } }
                expr.namedArgs.forEach { na -> findSourceParamRef(na.value, names)?.let { return it } }
            }
            is ExprAst.RangeExpr -> {
                findSourceParamRef(expr.start, names)?.let { return it }
                findSourceParamRef(expr.endValue, names)?.let { return it }
            }
            is ExprAst.IndexExpr -> {
                findSourceParamRef(expr.target, names)?.let { return it }
                findSourceParamRef(expr.index, names)?.let { return it }
            }
            is ExprAst.IsExpr -> findSourceParamRef(expr.target, names)?.let { return it }
            else -> Unit
        }
        return null
    }

    /** List 形态下的元素类型检查（E704 / E705 / E706）。 */
    private fun checkListElement(
        element: ElementAst,
        param: ElementAst.SourceParam,
        elemType: TypeAst?,
    ) {
        val explicit = param.type
        // E704：形参类型与元素类型不匹配（仅在二者均已知时严格比较）
        if (explicit != null && elemType != null && !typeMatches(explicit, elemType)) {
            diag.error(
                DslError.E704_CONTAINER_PARAM_TYPE_MISMATCH,
                param.position,
                element.kind, param.name, typeDisplay(explicit), typeDisplay(elemType),
            )
        }
        val hasExplicitKey = element.props.any { it.name == "key" }
        if (hasExplicitKey) return

        when {
            elemType is TypeAst.Named && elemType.name in dataDecls -> {
                val decl = dataDecls.getValue(elemType.name)
                if (decl.fields.none { it.name == "id" }) {
                    diag.error(
                        DslError.E705_CONTAINER_DATA_MISSING_ID,
                        element.position,
                        element.kind, decl.name,
                    )
                }
            }
            elemType is TypeAst.Generic && elemType.name == "Map" -> {
                diag.error(
                    DslError.E706_CONTAINER_KEY_REQUIRED,
                    element.position,
                    element.kind, typeDisplay(elemType),
                )
            }
            elemType is TypeAst.Named && elemType.name == "Any" -> {
                diag.error(
                    DslError.E706_CONTAINER_KEY_REQUIRED,
                    element.position,
                    element.kind, "Any",
                )
            }
            elemType == null -> {
                diag.error(
                    DslError.E706_CONTAINER_KEY_REQUIRED,
                    element.position,
                    element.kind, "未知",
                )
            }
            else -> Unit
        }
    }

    // -------- 类型推断辅助 --------

    /** source 表达式静态类型（仅识别明确可判定的 List / Map / 非容器形态）。 */
    private sealed class SourceType {
        data class ListT(val elem: TypeAst?) : SourceType()
        data class MapT(val key: TypeAst?, val value: TypeAst?) : SourceType()
        data class Other(val display: String) : SourceType()
    }

    /**
     * 推断 source 表达式静态类型。
     *  - `state.x`：从 state defaults 类型表查
     *  - `ListLiteralExpr`：List，元素类型未知
     *  - `MapLiteralExpr`：Map，元素类型未知
     *  - 其它形态：返回 null（保守，不报 E702）
     */
    private fun inferSourceType(expr: ExprAst): SourceType? = when (expr) {
        is ExprAst.StateRef -> stateTypes[expr.name]?.let { fromTypeAst(it) }
        is ExprAst.ListLiteralExpr -> SourceType.ListT(elem = null)
        is ExprAst.MapLiteralExpr -> SourceType.MapT(key = null, value = null)
        else -> null
    }

    private fun fromTypeAst(t: TypeAst): SourceType = when {
        t is TypeAst.Generic && t.name == "List" ->
            SourceType.ListT(t.args.firstOrNull())
        t is TypeAst.Generic && t.name == "Map" ->
            SourceType.MapT(t.args.getOrNull(0), t.args.getOrNull(1))
        else -> SourceType.Other(typeDisplay(t))
    }

    /** 简化的类型匹配：命名相等 + 泛型实参逐项递归比较；可空差异容忍。 */
    private fun typeMatches(a: TypeAst, b: TypeAst): Boolean {
        if (a is TypeAst.Named && b is TypeAst.Named) return a.name == b.name
        if (a is TypeAst.Generic && b is TypeAst.Generic) {
            if (a.name != b.name) return false
            if (a.args.size != b.args.size) return false
            return a.args.indices.all { typeMatches(a.args[it], b.args[it]) }
        }
        return typeDisplay(a) == typeDisplay(b)
    }

    private fun typeDisplay(t: TypeAst): String = when (t) {
        is TypeAst.Named -> if (t.nullable) "${t.name}?" else t.name
        is TypeAst.Generic -> {
            val args = t.args.joinToString(", ") { typeDisplay(it) }
            if (t.nullable) "${t.name}<$args>?" else "${t.name}<$args>"
        }
        is TypeAst.Function -> "(..)->.."
    }
}
